export interface LinkMetadata {
  url: string;
  domain: string;
  displayUrl: string;
  title: string;
  description: string;
  type: 'zoom' | 'youtube' | 'general';
  image?: string;
  isLoading?: boolean;
}

const STORAGE_CACHE_KEY = 'minipt_link_meta_cache';

// In-memory cache for dynamic client-side fetched metadata
const metadataCache = new Map<string, Partial<LinkMetadata>>();
const metadataListeners = new Map<string, Set<(meta: Partial<LinkMetadata>) => void>>();

// Initialize cache from localStorage
if (typeof window !== 'undefined' && window.localStorage) {
  try {
    const saved = localStorage.getItem(STORAGE_CACHE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (typeof parsed === 'object' && parsed !== null) {
        Object.entries(parsed).forEach(([k, v]) => {
          if (v && typeof v === 'object') {
            metadataCache.set(k, v as Partial<LinkMetadata>);
          }
        });
      }
    }
  } catch {
    // Ignore storage parse errors
  }
}

function persistCacheToStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const obj: Record<string, Partial<LinkMetadata>> = {};
    metadataCache.forEach((v, k) => {
      obj[k] = v;
    });
    localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(obj));
  } catch {
    // Ignore storage write errors
  }
}

const IGNORED_SLUGS = new Set([
  'in',
  'us',
  'uk',
  'ca',
  'au',
  'de',
  'fr',
  'es',
  'it',
  'nl',
  'se',
  'no',
  'dk',
  'fi',
  'pt',
  'pl',
  'ru',
  'cn',
  'jp',
  'kr',
  'br',
  'mx',
  'ar',
  'za',
  'sg',
  'nz',
  'ie',
  'ae',
  'sa',
  'index',
  'home',
  'app',
  'login',
  'signup',
  'default',
  'en',
  'en-us',
  'en-gb',
  'en-in',
  'p',
  'd',
  'v',
  'watch',
  'item',
  'page',
  'post',
]);

const KNOWN_DOMAINS: Record<string, string> = {
  'store.google.com': 'Google Store',
  'google.com': 'Google',
  'mail.google.com': 'Google Mail',
  'docs.google.com': 'Google Docs',
  'drive.google.com': 'Google Drive',
  'youtube.com': 'YouTube',
  'youtu.be': 'YouTube',
  'github.com': 'GitHub',
  'zoom.us': 'Zoom',
  'healthline.com': 'Healthline',
  'webmd.com': 'WebMD',
  'myfitnesspal.com': 'MyFitnessPal',
  'wikipedia.org': 'Wikipedia',
  'en.wikipedia.org': 'Wikipedia',
  'apple.com': 'Apple',
  'amazon.com': 'Amazon',
  'medium.com': 'Medium',
  'instagram.com': 'Instagram',
  'facebook.com': 'Facebook',
  'linkedin.com': 'LinkedIn',
  'twitter.com': 'Twitter / X',
  'x.com': 'X',
  'bbc.com': 'BBC',
  'nytimes.com': 'The New York Times',
  'spotify.com': 'Spotify',
};

/**
 * Format domain into readable site name
 */
export function formatDomainToTitle(domain: string): string {
  const cleanDomain = domain.toLowerCase().replace(/^www\./, '');
  if (KNOWN_DOMAINS[cleanDomain]) {
    return KNOWN_DOMAINS[cleanDomain];
  }

  // Handle subdomain e.g. store.something.com
  const parts = cleanDomain.split('.');
  if (parts.length >= 3 && parts[parts.length - 2].length > 2) {
    const sub = parts[0];
    const main = parts[1];
    return `${main.charAt(0).toUpperCase() + main.slice(1)} ${sub.charAt(0).toUpperCase() + sub.slice(1)}`;
  }

  if (parts.length >= 2) {
    const main = parts[0];
    return main.charAt(0).toUpperCase() + main.slice(1);
  }

  return cleanDomain.charAt(0).toUpperCase() + cleanDomain.slice(1);
}

/**
 * Format a slug into capitalized readable words
 * e.g. "post-workout-recovery-tips" -> "Post-workout recovery tips"
 */
function formatSlugToTitle(slug: string): string {
  if (!slug) return '';
  const clean = slug
    .replace(/\.[a-zA-Z0-9]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  if (!clean) return '';
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

/**
 * Format domain and path into clean display URL
 */
export function formatDisplayUrl(urlStr: string, maxLen = 45): string {
  try {
    const parsed = new URL(urlStr.startsWith('http') ? urlStr : `https://${urlStr}`);
    const cleanHost = parsed.hostname.replace(/^www\./, '');
    const cleanPath = parsed.pathname;
    if (cleanPath === '/' || cleanPath === '') {
      return cleanHost;
    }
    const full = `${cleanHost}${cleanPath}`;
    if (full.length > maxLen) {
      return full.slice(0, maxLen - 3) + '...';
    }
    return full;
  } catch {
    const raw = urlStr.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    if (raw.length > maxLen) {
      return raw.slice(0, maxLen - 3) + '...';
    }
    return raw;
  }
}

/**
 * Synchronous initial metadata resolver
 */
export function getInitialLinkMetadata(rawUrl: string, contextText?: string): LinkMetadata {
  const url = rawUrl.trim();
  let domain = url;
  let pathname = '';

  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    domain = parsed.hostname.replace(/^www\./, '');
    pathname = parsed.pathname;
  } catch {
    domain = url.split('/')[0].replace(/^www\./, '');
  }

  const lowerUrl = url.toLowerCase();
  const lowerDomain = domain.toLowerCase();

  // 1. ZOOM LINK (Exact Figma copy: "Check-in call (Zoom)" / "Join our check-in to review your progress and the next steps")
  if (
    lowerUrl.includes('zoom.us') ||
    lowerDomain.includes('zoom.us') ||
    lowerDomain.includes('zoom')
  ) {
    return {
      url,
      domain,
      displayUrl: formatDisplayUrl(url),
      title: 'Check-in call (Zoom)',
      description: 'Join our check-in to review your progress and the next steps',
      type: 'zoom',
    };
  }

  // Check cache for already fetched metadata
  const cached = metadataCache.get(url);
  if (cached && cached.title) {
    return {
      url,
      domain,
      displayUrl: formatDisplayUrl(url),
      title: cached.title,
      description: cached.description || '',
      type:
        (cached.type as 'zoom' | 'youtube' | 'general') ||
        (lowerUrl.includes('youtube') ? 'youtube' : 'general'),
      image: cached.image,
      isLoading: false,
    };
  }

  // 2. YOUTUBE LINK
  const isYoutube =
    lowerUrl.includes('youtube.com') ||
    lowerUrl.includes('youtu.be') ||
    lowerDomain.includes('youtube') ||
    lowerDomain.includes('youtu.be');

  if (isYoutube) {
    return {
      url,
      domain,
      displayUrl: formatDisplayUrl(url),
      title: 'Squat form tutorial',
      description: 'Join our check-in to review your progress and the next steps',
      type: 'youtube',
      isLoading: true,
    };
  }

  // 3. GENERAL / HEALTH / FITNESS / ARTICLE LINKS
  const pathSegments = pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1] || '';
  const isIgnoredSlug =
    !lastSegment || lastSegment.length <= 2 || IGNORED_SLUGS.has(lastSegment.toLowerCase());

  let fallbackTitle = '';
  if (!isIgnoredSlug) {
    fallbackTitle = formatSlugToTitle(lastSegment);
  }
  if (!fallbackTitle) {
    fallbackTitle = formatDomainToTitle(domain);
  }

  // Initial description from context or clean domain note
  let fallbackDesc = '';
  if (contextText && contextText.trim()) {
    const cleanContext = contextText
      .replace(url, '')
      .replace(/https?:\/\/[^\s]+/g, '')
      .trim();
    if (cleanContext.length > 5) {
      fallbackDesc = cleanContext.length > 90 ? cleanContext.slice(0, 87) + '...' : cleanContext;
    }
  }

  return {
    url,
    domain,
    displayUrl: formatDisplayUrl(url),
    title: fallbackTitle,
    description: fallbackDesc,
    type: 'general',
    isLoading: true,
  };
}

/**
 * Client-side dynamic metadata fetcher using multi-tiered public OpenGraph / oEmbed engines
 */
export async function fetchClientLinkMetadata(url: string): Promise<Partial<LinkMetadata> | null> {
  const lowerUrl = url.toLowerCase();

  // Zoom is static per requirements
  if (lowerUrl.includes('zoom.us') || lowerUrl.includes('zoom')) {
    return null;
  }

  // Check cache first
  if (metadataCache.has(url) && metadataCache.get(url)?.title) {
    return metadataCache.get(url) || null;
  }

  // 1. YouTube oEmbed
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    try {
      const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
      const res = await fetch(oembedUrl);
      if (res.ok) {
        const data = await res.json();
        const result: Partial<LinkMetadata> = {
          title: data.title || 'Squat form tutorial',
          description: data.author_name
            ? `Video by ${data.author_name} on YouTube`
            : 'Watch the video guide and technique breakdown on YouTube',
          image: data.thumbnail_url,
          type: 'youtube',
        };
        metadataCache.set(url, result);
        persistCacheToStorage();
        notifyListeners(url, result);
        return result;
      }
    } catch {
      // Fall through to other providers
    }
  }

  // 2. Microlink OpenGraph API (Industry standard for web link metadata)
  try {
    const microlinkUrl = `https://api.microlink.io?url=${encodeURIComponent(url)}`;
    const mlRes = await fetch(microlinkUrl, { signal: AbortSignal.timeout(5000) });
    if (mlRes.ok) {
      const mlData = await mlRes.json();
      if (mlData.status === 'success' && mlData.data) {
        const d = mlData.data;
        const result: Partial<LinkMetadata> = {
          title: d.title || undefined,
          description: d.description || undefined,
          image: d.image?.url || undefined,
        };
        if (result.title) {
          metadataCache.set(url, result);
          persistCacheToStorage();
          notifyListeners(url, result);
          return result;
        }
      }
    }
  } catch {
    // Fall through
  }

  // 3. NoEmbed Provider
  try {
    const noembedUrl = `https://noembed.com/embed?url=${encodeURIComponent(url)}`;
    const noembedRes = await fetch(noembedUrl, { signal: AbortSignal.timeout(4000) });
    if (noembedRes.ok) {
      const data = await noembedRes.json();
      if (data.title && !data.error) {
        const result: Partial<LinkMetadata> = {
          title: data.title,
          description:
            data.description ||
            (data.author_name
              ? `By ${data.author_name} on ${data.provider_name || 'web'}`
              : undefined),
          image: data.thumbnail_url,
        };
        metadataCache.set(url, result);
        persistCacheToStorage();
        notifyListeners(url, result);
        return result;
      }
    }
  } catch {
    // Fall through
  }

  // 4. AllOrigins Proxy with in-browser DOMParser
  try {
    const allOriginsUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
    const aoRes = await fetch(allOriginsUrl, { signal: AbortSignal.timeout(5000) });
    if (aoRes.ok) {
      const aoData = await aoRes.json();
      if (aoData.contents && typeof aoData.contents === 'string') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(aoData.contents, 'text/html');
        const ogTitle =
          doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
          doc.querySelector('meta[name="twitter:title"]')?.getAttribute('content') ||
          doc.querySelector('title')?.textContent?.trim();

        const ogDesc =
          doc.querySelector('meta[property="og:description"]')?.getAttribute('content') ||
          doc.querySelector('meta[name="twitter:description"]')?.getAttribute('content') ||
          doc.querySelector('meta[name="description"]')?.getAttribute('content');

        const ogImage =
          doc.querySelector('meta[property="og:image"]')?.getAttribute('content') ||
          doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content');

        if (ogTitle) {
          const result: Partial<LinkMetadata> = {
            title: ogTitle,
            description: ogDesc || undefined,
            image: ogImage || undefined,
          };
          metadataCache.set(url, result);
          persistCacheToStorage();
          notifyListeners(url, result);
          return result;
        }
      }
    }
  } catch {
    // Fallback
  }

  return null;
}

function notifyListeners(url: string, meta: Partial<LinkMetadata>) {
  const listeners = metadataListeners.get(url);
  if (listeners) {
    listeners.forEach((fn) => fn(meta));
  }
}

export function subscribeLinkMetadata(
  url: string,
  callback: (meta: Partial<LinkMetadata>) => void,
): () => void {
  if (!metadataListeners.has(url)) {
    metadataListeners.set(url, new Set());
  }
  metadataListeners.get(url)!.add(callback);

  // If already cached, call immediately
  if (metadataCache.has(url)) {
    callback(metadataCache.get(url)!);
  } else {
    // Trigger background client-side fetch
    fetchClientLinkMetadata(url);
  }

  return () => {
    metadataListeners.get(url)?.delete(callback);
    if (metadataListeners.get(url)?.size === 0) {
      metadataListeners.delete(url);
    }
  };
}
